import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserLayout } from '../components/User/UserLayout';
import { useAuthStore } from '../store/authStore';
import { useEffectivePlan } from '../store/accountStore';
import { normalizeTier } from '../constants/planLimits';
import {
  Check,
  Minus,
  Crown,
  Zap,
  Shield,
  Sparkles,
  Building2,
  ArrowRight,
  Layers,
  Radio,
  ShieldCheck,
  BarChart3,
  LayoutGrid,
  Headphones,
} from 'lucide-react';
import {
  PLANS,
  USD_TO_THB_RATE,
  effectiveMonthlyUsd,
  formatThb,
  formatUsd,
  tierOrder,
  usdToThb,
  type Cycle,
  type PlanId,
  type Tier,
} from '../constants/pricing';

type Cell = boolean | 'value';

interface FeatureRow {
  key: string; // lookup key under `upgrade.feature.<key>`
  hasValue?: boolean;
  cells: Record<Tier, Cell>;
}

interface FeatureSection {
  headerKey: string; // lookup key under `upgrade.feature.<headerKey>`
  icon: React.ReactNode;
  rows: FeatureRow[];
}

/**
 * Authoritative feature comparison matrix — kept in 1:1 lockstep with
 * `server/middleware/planLimits.js` and `client/src/constants/planLimits.ts`.
 *
 *   free:       1 page, 0 domains, 1 pixel, 0 team seats, 100 submissions, 100 MB, 7d retention
 *   pro:        5 pages, 3 domains, 3 pixels, 2 team seats, 5K submissions, 5 GB, 90d retention,
 *               affiliatePostback, redirect, geoFence, topCities, dwellTime, csvExport
 *   agency:     20 pages, 15 domains, unlimited pixels, 5 team seats, 50K submissions, 50 GB, 365d retention,
 *               metaCapi, audienceExport, cloaking, aiSafePages (20/mo), botLogs, customCss
 *   enterprise: unlimited pages/domains/pixels/seats, unlimited submissions, 500 GB, 2yr retention,
 *               gtmAutoSync, capiReplay, aiSafePages (100/mo), full white-label
 */
const FEATURE_SECTIONS: FeatureSection[] = [
  {
    headerKey: 'headerCore',
    icon: <Layers className="w-4 h-4 text-indigo-600" />,
    rows: [
      { key: 'pages',         hasValue: true, cells: { free: 'value', pro: 'value', agency: 'value', enterprise: 'value' } },
      { key: 'customDomains', hasValue: true, cells: { free: false,   pro: 'value', agency: 'value', enterprise: 'value' } },
      { key: 'subAccounts',   hasValue: true, cells: { free: false,   pro: 'value', agency: 'value', enterprise: 'value' } },
      { key: 'submissions',   hasValue: true, cells: { free: 'value', pro: 'value', agency: 'value', enterprise: 'value' } },
      { key: 'storage',       hasValue: true, cells: { free: 'value', pro: 'value', agency: 'value', enterprise: 'value' } },
      { key: 'watermark',     hasValue: true, cells: { free: 'value', pro: 'value', agency: 'value', enterprise: 'value' } },
    ],
  },
  {
    headerKey: 'headerTracking',
    icon: <Radio className="w-4 h-4 text-blue-600" />,
    rows: [
      { key: 'fbPixels',          hasValue: true, cells: { free: 'value', pro: 'value', agency: 'value', enterprise: 'value' } },
      { key: 'pixelManager',      cells: { free: true,  pro: true,  agency: true,  enterprise: true  } },
      { key: 'ga',                cells: { free: true,  pro: true,  agency: true,  enterprise: true  } },
      { key: 'affiliatePostback', hasValue: true, cells: { free: false, pro: 'value', agency: 'value', enterprise: 'value' } },
      { key: 'devDoc',            cells: { free: false, pro: true,  agency: true,  enterprise: true  } },
      { key: 'metaCapi',          hasValue: true, cells: { free: false, pro: false, agency: 'value', enterprise: 'value' } },
      { key: 'liveCapi',          cells: { free: false, pro: false, agency: true,  enterprise: true  } },
      { key: 'deterministicId',   cells: { free: false, pro: false, agency: true,  enterprise: true  } },
      { key: 'externalId',        cells: { free: false, pro: false, agency: true,  enterprise: true  } },
      { key: 'audienceExport',    cells: { free: false, pro: false, agency: true,  enterprise: true  } },
      { key: 'gtmAuto',           cells: { free: false, pro: false, agency: false, enterprise: true  } },
      { key: 'capiReplay',        cells: { free: false, pro: false, agency: false, enterprise: true  } },
    ],
  },
  {
    headerKey: 'headerCloaking',
    icon: <ShieldCheck className="w-4 h-4 text-purple-600" />,
    rows: [
      { key: 'geoFence',       hasValue: true, cells: { free: false, pro: 'value', agency: 'value', enterprise: 'value' } },
      { key: 'deviceRedirect', hasValue: true, cells: { free: false, pro: 'value', agency: 'value', enterprise: 'value' } },
      { key: 'cloaking',       cells: { free: false, pro: false, agency: true,  enterprise: true  } },
      { key: 'aiSafePages',    hasValue: true, cells: { free: false, pro: false, agency: 'value', enterprise: 'value' } },
      { key: 'staticSafe',     cells: { free: false, pro: false, agency: true,  enterprise: true  } },
      { key: 'cloakingDash',   cells: { free: false, pro: false, agency: true,  enterprise: true  } },
      { key: 'botLogs',        cells: { free: false, pro: false, agency: true,  enterprise: true  } },
    ],
  },
  {
    headerKey: 'headerAnalytics',
    icon: <BarChart3 className="w-4 h-4 text-emerald-600" />,
    rows: [
      { key: 'retention', hasValue: true, cells: { free: 'value', pro: 'value', agency: 'value', enterprise: 'value' } },
      { key: 'topCities', cells: { free: false, pro: true,  agency: true,  enterprise: true  } },
      { key: 'dwellTime', cells: { free: false, pro: true,  agency: true,  enterprise: true  } },
      { key: 'csvExport', cells: { free: false, pro: true,  agency: true,  enterprise: true  } },
    ],
  },
  {
    headerKey: 'headerBuilder',
    icon: <LayoutGrid className="w-4 h-4 text-amber-600" />,
    rows: [
      { key: 'components', cells: { free: true,  pro: true,  agency: true,  enterprise: true  } },
      { key: 'customCss',  cells: { free: false, pro: false, agency: true,  enterprise: true  } },
    ],
  },
];

/**
 * Key highlights shown directly on each Pricing Card so operators can
 * scan the tier ladder at a glance before reading the full matrix below.
 */
const CARD_HIGHLIGHTS: Record<Tier, string[]> = {
  free: [
    'pages',
    'fbPixels',
    'components',
    'ga',
    'retention',
    'watermark',
  ],
  pro: [
    'pages',
    'customDomains',
    'fbPixels',
    'subAccounts',
    'affiliatePostback',
    'deviceRedirect',
    'geoFence',
    'watermark',
  ],
  agency: [
    'pages',
    'customDomains',
    'fbPixels',
    'subAccounts',
    'cloaking',
    'aiSafePages',
    'metaCapi',
    'audienceExport',
  ],
  enterprise: [
    'pages',
    'customDomains',
    'fbPixels',
    'subAccounts',
    'gtmAuto',
    'capiReplay',
    'aiSafePages',
    'watermark',
  ],
};

const TIER_ORDER: Tier[] = ['free', 'pro', 'agency', 'enterprise'];

const TIER_ICON: Record<Tier, React.ReactNode> = {
  free:       <Sparkles className="w-5 h-5" />,
  pro:        <Zap className="w-5 h-5" />,
  agency:     <Shield className="w-5 h-5" />,
  enterprise: <Building2 className="w-5 h-5" />,
};

const TIER_ACCENT: Record<
  Tier,
  {
    border: string;
    bg: string;
    badgeBg: string;
    button: string;
    text: string;
    colHighlight: string;
  }
> = {
  free: {
    border: 'border-slate-200',
    bg: 'bg-white',
    badgeBg: 'bg-slate-100 text-slate-700',
    button: 'bg-slate-900 hover:bg-slate-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white',
    text: 'text-slate-700',
    colHighlight: '',
  },
  pro: {
    border: 'border-blue-200',
    bg: 'bg-gradient-to-b from-blue-50/70 via-white to-white',
    badgeBg: 'bg-blue-100 text-blue-700',
    button: 'bg-blue-600 hover:bg-blue-700',
    text: 'text-blue-600',
    colHighlight: '',
  },
  agency: {
    border: 'border-purple-400 ring-2 ring-purple-500/20',
    bg: 'bg-gradient-to-b from-purple-50/90 via-white to-white',
    badgeBg: 'bg-purple-100 text-purple-700',
    button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-95',
    text: 'text-purple-600',
    colHighlight: 'bg-purple-50/35',
  },
  enterprise: {
    border: 'border-amber-300',
    bg: 'bg-gradient-to-b from-amber-50/70 via-white to-white',
    badgeBg: 'bg-amber-100 text-amber-800',
    button: 'bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-95',
    text: 'text-amber-600',
    colHighlight: '',
  },
};

const POPULAR_TIER: Tier = 'agency';

export const UpgradePlan: React.FC = () => {
  const { t, i18n } = useTranslation('billing');
  const { user } = useAuthStore();
  const effectivePlan = useEffectivePlan();
  const navigate = useNavigate();

  const [cycle, setCycle] = useState<Cycle>('monthly');

  const currentTier: Tier = normalizeTier(effectivePlan || user?.subscription);

  const planIdFor = (tier: Tier): PlanId | null => {
    if (tier === 'free') return null;
    return `${tier}_${cycle}` as PlanId;
  };

  const handleCheckout = (tier: Tier) => {
    const planId = planIdFor(tier);
    if (!planId) return;
    navigate(`/payment-checkout?plan=${planId}`);
  };

  const grandfatherDate = useMemo(() => {
    const raw = (user as unknown as { grandfatheredUntil?: string })?.grandfatheredUntil;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [user, i18n.language]);

  return (
    <UserLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-xs font-semibold mb-3">
            <Crown className="w-3.5 h-3.5" />
            <span>
              {t('upgrade.currentPlan')}: <strong className="uppercase">{t(`upgrade.tier.${currentTier}.name`)}</strong>
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-3">
            {t('upgrade.title')}
          </h1>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            {t('upgrade.subtitle')}
          </p>
        </div>

        {/* Grandfather banner */}
        {grandfatherDate && (
          <div className="max-w-3xl mx-auto mb-8 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm flex items-start gap-3">
            <Crown className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p>{t('upgrade.grandfather.banner', { date: grandfatherDate })}</p>
          </div>
        )}

        {/* Monthly / Yearly toggle */}
        <div className="flex flex-col items-center justify-center mb-10 gap-2">
          <div className="inline-flex items-center bg-gray-100 p-1.5 rounded-full border border-gray-200 shadow-inner">
            <button
              type="button"
              onClick={() => setCycle('monthly')}
              className={`px-6 py-2 text-sm font-semibold rounded-full transition-all ${
                cycle === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('upgrade.billingMonthly')}
            </button>
            <button
              type="button"
              onClick={() => setCycle('yearly')}
              className={`px-6 py-2 text-sm font-semibold rounded-full transition-all flex items-center gap-2 ${
                cycle === 'yearly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('upgrade.billingYearly')}
              <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                {t('upgrade.saveBadge')}
              </span>
            </button>
          </div>
          <p className="text-xs text-gray-500">
            {t('checkout.summary.fxNote', { rate: USD_TO_THB_RATE })}
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-16 items-stretch">
          {TIER_ORDER.map((tier) => (
            <PricingCard
              key={tier}
              tier={tier}
              cycle={cycle}
              isCurrent={currentTier === tier}
              isPopular={tier === POPULAR_TIER}
              onCheckout={() => handleCheckout(tier)}
              currentTier={currentTier}
            />
          ))}
        </div>

        {/* Full comparison matrix */}
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {t('upgrade.compareHeading', { defaultValue: 'เปรียบเทียบฟีเจอร์ทั้งหมดแบบละเอียด' })}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('upgrade.compareSubheading', {
                defaultValue: 'ตรวจสอบโควตาและเครื่องมือที่ได้ในแต่ละแพ็กเกจแบบครบทุกจุด',
              })}
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50/90 border-b border-gray-200">
                  <th className="text-left px-5 py-4 font-semibold text-gray-700 w-[34%] min-w-[240px]">
                    {t('upgrade.featureColumn', { defaultValue: 'ฟีเจอร์ / ขีดจำกัด' })}
                  </th>
                  {TIER_ORDER.map((tier) => {
                    const isPop = tier === POPULAR_TIER;
                    const isCur = tier === currentTier;
                    const planId = tier === 'free' ? null : (`${tier}_${cycle}` as PlanId);
                    const monthlyUsd = planId ? effectiveMonthlyUsd(planId) : 0;
                    return (
                      <th
                        key={tier}
                        className={`text-center px-4 py-4 font-semibold text-gray-900 min-w-[140px] ${
                          isPop ? 'bg-purple-50/60' : ''
                        }`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex items-center justify-center gap-1.5">
                            <span className={TIER_ACCENT[tier].text}>{TIER_ICON[tier]}</span>
                            <span className="text-base font-bold">{t(`upgrade.tier.${tier}.name`)}</span>
                          </div>
                          <div className="text-xs font-medium text-gray-500">
                            {tier === 'free' ? '$0' : `${formatUsd(monthlyUsd)}${t('upgrade.perMonth')}`}
                          </div>
                          {isCur && (
                            <span className="mt-0.5 inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                              {t('upgrade.currentPlan')}
                            </span>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {FEATURE_SECTIONS.map((section) => (
                  <React.Fragment key={section.headerKey}>
                    <tr className="bg-gray-50/80 border-y border-gray-200">
                      <td
                        colSpan={5}
                        className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-700"
                      >
                        <div className="flex items-center gap-2">
                          {section.icon}
                          <span>{t(`upgrade.feature.${section.headerKey}`)}</span>
                        </div>
                      </td>
                    </tr>
                    {section.rows.map((row) => (
                      <FeatureMatrixRow key={row.key} row={row} />
                    ))}
                  </React.Fragment>
                ))}

                {/* Support section */}
                <tr className="bg-gray-50/80 border-y border-gray-200">
                  <td
                    colSpan={5}
                    className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-700"
                  >
                    <div className="flex items-center gap-2">
                      <Headphones className="w-4 h-4 text-rose-600" />
                      <span>{t('upgrade.feature.headerSupport')}</span>
                    </div>
                  </td>
                </tr>
                <SupportRow rowKey="doc"        cells={{ free: true,  pro: true,    agency: true,    enterprise: true }} />
                <SupportRow rowKey="email"      cells={{ free: false, pro: 'value', agency: 'value', enterprise: 'value' }} />
                <SupportRow rowKey="channel"    cells={{ free: false, pro: 'value', agency: 'value', enterprise: 'value' }} />
                <SupportRow rowKey="onboarding" cells={{ free: false, pro: false,   agency: 'value', enterprise: 'value' }} />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </UserLayout>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Pricing Card
// ────────────────────────────────────────────────────────────────────

interface PricingCardProps {
  tier: Tier;
  cycle: Cycle;
  isCurrent: boolean;
  isPopular: boolean;
  currentTier: Tier;
  onCheckout: () => void;
}

const PricingCard: React.FC<PricingCardProps> = ({
  tier,
  cycle,
  isCurrent,
  isPopular,
  currentTier,
  onCheckout,
}) => {
  const { t } = useTranslation('billing');
  const accent = TIER_ACCENT[tier];

  const planId: PlanId | null = tier === 'free' ? null : (`${tier}_${cycle}` as PlanId);
  const plan = planId ? PLANS[planId] : null;

  const usdHeadline = plan ? effectiveMonthlyUsd(planId!) : 0;
  const thbCheckout = plan ? usdToThb(plan.usd) : 0;

  const isDowngrade = !isCurrent && tier !== 'free' && tierOrder(tier) < tierOrder(currentTier);

  return (
    <div
      className={`relative flex flex-col rounded-2xl border-2 p-6 transition-all ${accent.border} ${accent.bg} ${
        isPopular ? 'shadow-xl xl:-translate-y-1' : 'shadow-sm hover:shadow-md'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 px-3.5 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-[11px] font-bold uppercase tracking-wider shadow-md whitespace-nowrap">
            <Sparkles className="w-3 h-3" />
            {t('upgrade.popular')}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2">
          <span className={`p-2 rounded-xl ${accent.badgeBg}`}>{TIER_ICON[tier]}</span>
          <h3 className="text-xl font-bold text-gray-900">{t(`upgrade.tier.${tier}.name`)}</h3>
        </div>
        {isCurrent && (
          <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
            {t('upgrade.currentPlan')}
          </span>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-5 min-h-[2.5rem] leading-relaxed">
        {t(`upgrade.tier.${tier}.tagline`)}
      </p>

      {/* Price */}
      <div className="mb-5 pb-5 border-b border-gray-200/80">
        {tier === 'free' ? (
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-extrabold text-gray-900">$0</span>
              <span className="text-sm font-medium text-gray-500">{t('upgrade.perMonth')}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              {t('upgrade.freeForever', { defaultValue: 'ใช้งานฟรีตลอดชีพ ไม่ต้องใช้บัตรเครดิต' })}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-extrabold text-gray-900">{formatUsd(usdHeadline)}</span>
              <span className="text-sm font-medium text-gray-500">{t('upgrade.perMonth')}</span>
            </div>
            {cycle === 'yearly' && plan ? (
              <p className="text-xs text-emerald-700 font-medium mt-1.5">
                {formatUsd(plan.usd)} {t('upgrade.billedAnnually')} ({formatThb(thbCheckout)})
              </p>
            ) : (
              plan && (
                <p className="text-xs text-gray-500 mt-1.5">
                  {t('upgrade.billedAtThb', { amount: formatThb(thbCheckout) })}
                </p>
              )
            )}
          </>
        )}
      </div>

      {/* CTA */}
      <div className="mb-6">
        {isCurrent ? (
          <div
            className={`w-full py-3 rounded-xl text-center text-sm font-semibold border ${accent.border} ${accent.text} bg-white`}
          >
            {t('upgrade.currentPlan')}
          </div>
        ) : tier === 'free' || isDowngrade ? (
          <div className="w-full py-3 rounded-xl text-center text-xs font-medium text-gray-500 bg-gray-100 px-3">
            {t('upgrade.downgradeNotAvailable')}
          </div>
        ) : (
          <button
            type="button"
            onClick={onCheckout}
            className={`w-full py-3 px-4 rounded-xl text-white text-sm font-semibold transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg ${accent.button}`}
          >
            <span>{t(`upgrade.tier.${tier}.cta`)}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Highlights */}
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
        {t('checkout.summary.includes')}
      </div>
      <ul className="space-y-2.5 text-sm text-gray-700 flex-grow">
        {CARD_HIGHLIGHTS[tier].map((featureKey) => (
          <CardHighlight key={featureKey} featureKey={featureKey} tier={tier} />
        ))}
      </ul>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Table Helpers
// ────────────────────────────────────────────────────────────────────

const CardHighlight: React.FC<{ featureKey: string; tier: Tier }> = ({ featureKey, tier }) => {
  const { t, i18n } = useTranslation('billing');

  const valueKey = `upgrade.feature.${featureKey}Value.${tier}`;
  const hasValue = i18n.exists(valueKey, { ns: 'billing' });
  const labelKey = `upgrade.feature.${featureKey}`;

  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
        <Check className="w-3 h-3 stroke-[2.5]" />
      </span>
      <span className="leading-snug">
        {hasValue ? (
          <>
            <strong className="font-semibold text-gray-900">{t(valueKey)}</strong>
            <span className="text-gray-600"> — {t(labelKey)}</span>
          </>
        ) : (
          <span className="text-gray-800">{t(labelKey)}</span>
        )}
      </span>
    </li>
  );
};

const FeatureMatrixRow: React.FC<{ row: FeatureRow }> = ({ row }) => {
  const { t } = useTranslation('billing');
  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50/60 transition-colors">
      <td className="px-5 py-3.5 text-gray-800 font-medium align-middle">
        {t(`upgrade.feature.${row.key}`)}
      </td>
      {TIER_ORDER.map((tier) => (
        <td
          key={tier}
          className={`px-4 py-3.5 text-center align-middle ${TIER_ACCENT[tier].colHighlight}`}
        >
          <CellRenderer cell={row.cells[tier]} valueKey={`upgrade.feature.${row.key}Value.${tier}`} />
        </td>
      ))}
    </tr>
  );
};

const SupportRow: React.FC<{ rowKey: string; cells: Record<Tier, Cell> }> = ({ rowKey, cells }) => {
  const { t } = useTranslation('billing');
  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50/60 transition-colors">
      <td className="px-5 py-3.5 text-gray-800 font-medium align-middle">
        {t(`upgrade.feature.support.${rowKey}`)}
      </td>
      {TIER_ORDER.map((tier) => (
        <td
          key={tier}
          className={`px-4 py-3.5 text-center align-middle ${TIER_ACCENT[tier].colHighlight}`}
        >
          <CellRenderer cell={cells[tier]} valueKey={`upgrade.feature.support.${rowKey}Value.${tier}`} />
        </td>
      ))}
    </tr>
  );
};

const CellRenderer: React.FC<{ cell: Cell; valueKey: string }> = ({ cell, valueKey }) => {
  const { t, i18n } = useTranslation('billing');
  if (cell === true) {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 mx-auto">
        <Check className="w-4 h-4 stroke-[2.5]" />
      </span>
    );
  }
  if (cell === false) {
    return (
      <span className="inline-flex items-center justify-center text-gray-300 mx-auto">
        <Minus className="w-4 h-4" />
      </span>
    );
  }
  if (!i18n.exists(valueKey, { ns: 'billing' })) {
    return (
      <span className="inline-flex items-center justify-center text-gray-300 mx-auto">
        <Minus className="w-4 h-4" />
      </span>
    );
  }
  return (
    <span className="inline-block text-gray-900 text-xs sm:text-sm font-semibold px-2.5 py-1 rounded-md bg-gray-100/80">
      {t(valueKey)}
    </span>
  );
};

export default UpgradePlan;
